import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import DashboardLayout from '../../components/feature/DashboardLayout';
import { useAuth } from '../../lib/api/auth';
import { settingsApi, apiKeyApi } from '../../lib/api/endpoints';
import type { UserInfo } from '../../lib/api/types';
import type { ApiKeyInfo } from '../../lib/api/endpoints';

export default function SettingsPage() {
  const navigate = useNavigate();
  const { user, logout, currentFamily } = useAuth();
  const [activeTab, setActiveTab] = useState<'account'>('account');
  const [loading, setLoading] = useState(true);

  // 模态框状态
  const [showLogoutConfirm, setShowLogoutConfirm] = useState(false);
  const [showPhoneModal, setShowPhoneModal] = useState(false);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [saveProfileSuccess, setSaveProfileSuccess] = useState(false);

  // API Key状态
  const [apiKeys, setApiKeys] = useState<ApiKeyInfo[]>([]);
  const [apiKeyLoading, setApiKeyLoading] = useState(false);
  const [showApiKeyModal, setShowApiKeyModal] = useState(false);
  const [newApiKey, setNewApiKey] = useState<string | null>(null);
  const [apiKeyName, setApiKeyName] = useState('默认Key');
  const [copySuccess, setCopySuccess] = useState(false);
  const [showApiKey, setShowApiKey] = useState(false);

  // 表单状态
  const [profile, setProfile] = useState({ nickname: '', avatar_url: '' });
  const [oldPwd, setOldPwd] = useState('');
  const [newPwd, setNewPwd] = useState('');
  const [confirmPwd, setConfirmPwd] = useState('');
  const [newPhone, setNewPhone] = useState('');
  const [verifyCode, setVerifyCode] = useState('');
  const [countdown, setCountdown] = useState(0);

  // 加载数据
  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      try {
        const [profileRes, notifRes] = await Promise.all([
          settingsApi.getProfile()
        ]);

        if (profileRes.code === 0 && profileRes.data) {
          setProfile({
            nickname: profileRes.data.nickname || user?.nickname || '',
            avatar_url: profileRes.data.avatar_url || ''
          });
        } else {
          setProfile(prev => ({
            ...prev,
            nickname: user?.nickname || ''
          }));
        }
      } catch (error) {
        console.error('Failed to load settings:', error);
        setProfile(prev => ({
          ...prev,
          nickname: user?.nickname || ''
        }));
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [user?.nickname]);

  // 加载API Key列表
  const loadApiKeys = async () => {
    setApiKeyLoading(true);
    try {
      const res = await apiKeyApi.listApiKeys();
      if (res.code === 0 && res.data) {
        setApiKeys(Array.isArray(res.data) ? res.data : (res.data as any).items || []);
      }
    } catch (error) {
      console.error('Failed to load API keys:', error);
    } finally {
      setApiKeyLoading(false);
    }
  };

  useEffect(() => {
    if (activeTab === 'account') {
      loadApiKeys();
    }
  }, [activeTab]);

  // 创建API Key
  const handleCreateApiKey = async () => {
    try {
      const res = await apiKeyApi.createApiKey(apiKeyName);
      if (res.code === 0 && res.data) {
        setNewApiKey(res.data.key);
        setShowApiKeyModal(true);
        loadApiKeys();
      }
    } catch (error) {
      console.error('Failed to create API key:', error);
    }
  };

  // 删除API Key
  const handleDeleteApiKey = async (keyId: string) => {
    if (!confirm('确定要删除这个API Key吗？删除后将无法恢复。')) return;
    try {
      const res = await apiKeyApi.deleteApiKey(keyId);
      if (res.code === 0) {
        loadApiKeys();
      }
    } catch (error) {
      console.error('Failed to delete API key:', error);
    }
  };

  // 复制到剪贴板
  const copyToClipboard = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopySuccess(true);
      setTimeout(() => setCopySuccess(false), 2000);
    } catch (error) {
      console.error('Failed to copy:', error);
    }
  };

  const startCountdown = () => {
    if (countdown > 0) return;
    setCountdown(60);
    const timer = setInterval(() => {
      setCountdown(prev => {
        if (prev <= 1) { clearInterval(timer); return 0; }
        return prev - 1;
      });
    }, 1000);
  };

  const handleSaveProfile = async () => {
    try {
      const res = await settingsApi.updateProfile(profile);
      if (res.code === 0) {
        setSaveProfileSuccess(true);
        setTimeout(() => setSaveProfileSuccess(false), 2000);
      }
    } catch (error) {
      console.error('Failed to save profile:', error);
    }
  };

  const handleUpdatePassword = async () => {
    if (newPwd !== confirmPwd) {
      alert('两次输入的密码不一致');
      return;
    }
    try {
      const res = await settingsApi.updatePassword({
        old_password: oldPwd,
        new_password: newPwd
      });
      if (res.code === 0) {
        setShowPasswordModal(false);
        setOldPwd('');
        setNewPwd('');
        setConfirmPwd('');
        alert('密码修改成功');
      }
    } catch (error) {
      console.error('Failed to update password:', error);
    }
  };

  const tabs = [
    { key: 'account' as const, label: '账号设置', icon: 'ri-user-settings-fill' }
  ] as const;

  if (loading) {
    return (
      <DashboardLayout>
        <div className="p-4 md:p-6 flex items-center justify-center min-h-[60vh]">
          <div className="text-center">
            <i className="ri-loader-4-line animate-spin text-4xl text-blue-500"></i>
            <p className="text-gray-400 mt-2">加载中...</p>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="p-4 md:p-6">
        {/* Header */}
        <div className="mb-5">
          <h1 className="text-xl font-black text-blue-900">系统设置</h1>
          <p className="text-gray-400 text-sm mt-0.5">管理账号信息与通知偏好</p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-4 gap-4">
          {/* Sidebar tabs */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-2xl p-3 space-y-1">
              {tabs.map(tab => (
                <button
                  key={tab.key}
                  onClick={() => setActiveTab(tab.key)}
                  className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium transition-all cursor-pointer text-left ${
                    activeTab === tab.key
                      ? 'bg-gradient-to-r from-blue-500 to-blue-700 text-white'
                      : 'text-gray-600 hover:bg-blue-50'
                  }`}
                >
                  <div className="w-5 h-5 flex items-center justify-center">
                    <i className={`${tab.icon} text-base`}></i>
                  </div>
                  <span className="whitespace-nowrap">{tab.label}</span>
                </button>
              ))}

              {/* User card */}
              <div className="mt-3 pt-3 border-t border-gray-50">
                <div className="px-3 py-3 bg-blue-50 rounded-xl">
                  <div className="flex items-center gap-2.5 mb-2">
                    <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-gradient-to-br from-blue-500 to-blue-700 flex-shrink-0">
                      <i className="ri-user-fill text-white text-base"></i>
                    </div>
                    <div>
                      <p className="text-sm font-bold text-blue-900">{user?.nickname || '用户'}</p>
                      <p className="text-xs text-gray-400">{currentFamily?.my_identity || '成员'}</p>
                    </div>
                  </div>
                  <div className="text-xs text-gray-400 flex items-center gap-1.5">
                    <i className="ri-smartphone-line text-xs"></i>
                    <span>{user?.phone ? user.phone.replace(/(\d{3})\d{4}(\d{4})/, '$1****$2') : ''}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Content */}
          <div className="lg:col-span-3">
            {/* Account Settings */}
            {activeTab === 'account' && (
              <div className="space-y-4">
                {/* 个人信息 */}
                <div className="bg-white rounded-2xl p-5">
                  <h3 className="font-bold text-blue-900 text-sm mb-4 flex items-center gap-2">
                    <i className="ri-user-line text-blue-500"></i>个人信息
                  </h3>
                  <div className="space-y-3">
                    <div>
                      <label className="text-xs text-gray-500 mb-1 block">显示昵称</label>
                      <input
                        value={profile.nickname}
                        onChange={e => setProfile({ ...profile, nickname: e.target.value })}
                        className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:border-blue-400"
                      />
                    </div>
                    <button
                      onClick={handleSaveProfile}
                      className="w-full py-2.5 bg-gradient-to-r from-blue-500 to-blue-700 text-white text-sm font-bold rounded-xl cursor-pointer hover:opacity-90 whitespace-nowrap flex items-center justify-center gap-2"
                    >
                      {saveProfileSuccess ? <><i className="ri-check-line"></i>已保存</> : <><i className="ri-save-line"></i>保存个人信息</>}
                    </button>
                  </div>
                </div>

                {/* 账号安全 */}
                <div className="bg-white rounded-2xl p-5">
                  <h3 className="font-bold text-blue-900 text-sm mb-4 flex items-center gap-2">
                    <i className="ri-shield-keyhole-fill text-emerald-500"></i>账号安全
                  </h3>
                  <div className="space-y-3">
                    {/* 手机号验证 */}
                    <div className="flex items-center justify-between p-4 bg-blue-50 rounded-xl">
                      <div className="flex items-center gap-3">
                        <div className="w-9 h-9 flex items-center justify-center rounded-lg bg-blue-100">
                          <i className="ri-smartphone-line text-blue-500 text-base"></i>
                        </div>
                        <div>
                          <p className="text-sm font-semibold text-blue-900">手机号验证</p>
                          <p className="text-xs text-gray-500 mt-0.5">
                            {user?.phone ? user.phone.replace(/(\d{3})\d{4}(\d{4})/, '$1****$2') : '未绑定'}
                          </p>
                        </div>
                      </div>
                      <button
                        onClick={() => setShowPhoneModal(true)}
                        className="flex items-center gap-1.5 px-4 py-2 bg-gradient-to-r from-blue-500 to-blue-700 text-white text-xs font-medium rounded-xl cursor-pointer hover:opacity-90 transition-opacity whitespace-nowrap"
                      >
                        <i className="ri-edit-line"></i>更改
                      </button>
                    </div>

                    {/* 登录密码 */}
                    <div className="flex items-center justify-between p-4 bg-blue-50 rounded-xl">
                      <div className="flex items-center gap-3">
                        <div className="w-9 h-9 flex items-center justify-center rounded-lg bg-blue-100">
                          <i className="ri-lock-password-line text-blue-500 text-base"></i>
                        </div>
                        <div>
                          <p className="text-sm font-semibold text-blue-900">登录密码</p>
                          <p className="text-xs text-gray-500 mt-0.5">上次修改：刚刚</p>
                        </div>
                      </div>
                      <button
                        onClick={() => setShowPasswordModal(true)}
                        className="flex items-center gap-1.5 px-4 py-2 bg-gradient-to-r from-blue-500 to-blue-700 text-white text-xs font-medium rounded-xl cursor-pointer hover:opacity-90 transition-opacity whitespace-nowrap"
                      >
                        <i className="ri-edit-line"></i>修改
                      </button>
                    </div>

                    {/* 微信绑定 */}
                    <div className="flex items-center justify-between p-4 bg-blue-50 rounded-xl">
                      <div className="flex items-center gap-3">
                        <div className="w-9 h-9 flex items-center justify-center rounded-lg bg-emerald-100">
                          <i className="ri-wechat-fill text-emerald-500 text-base"></i>
                        </div>
                        <div>
                          <p className="text-sm font-semibold text-blue-900">微信绑定</p>
                          <p className="text-xs text-gray-500 mt-0.5">未绑定</p>
                        </div>
                      </div>
                      <button className="flex items-center gap-1.5 px-4 py-2 bg-gradient-to-r from-emerald-500 to-emerald-600 text-white text-xs font-medium rounded-xl cursor-pointer hover:opacity-90 transition-opacity whitespace-nowrap">
                        <i className="ri-wechat-fill"></i>绑定
                      </button>
                    </div>
                  </div>
                </div>

                {/* API Key管理 */}
                <div className="bg-white rounded-2xl p-5">
                  <h3 className="font-bold text-blue-900 text-sm mb-4 flex items-center gap-2">
                    <i className="ri-key-2-fill text-violet-500"></i>API Key
                  </h3>
                  <p className="text-xs text-gray-400 mb-4">
                    API Key用于第三方AI助手（如Claude Desktop）连接习惯星球。
                  </p>

                  {/* 当前API Key */}
                  {apiKeyLoading ? (
                    <div className="flex items-center justify-center py-6">
                      <i className="ri-loader-4-line animate-spin text-2xl text-blue-500"></i>
                    </div>
                  ) : apiKeys.length > 0 ? (
                    <div className="bg-violet-50 rounded-xl p-4 mb-4">
                      <div className="flex items-center gap-3 mb-3">
                        <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-violet-100">
                          <i className="ri-key-2-line text-violet-500 text-lg"></i>
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-semibold text-blue-900">当前API Key</p>
                        </div>
                      </div>
                      {/* API Key显示框 */}
                      <div className="bg-[#f5f5f5] rounded-xl h-10 px-4 flex items-center gap-2 w-full">
                        <p className="flex-1 min-w-0 font-mono text-sm text-[#646a73] whitespace-nowrap overflow-hidden text-ellipsis">
                          {showApiKey
                            ? (apiKeys[0] as any).key || apiKeys[0].key_prefix
                            : ((apiKeys[0] as any).key || apiKeys[0].key_prefix).replace(/(.{10})(.*)(.{4})/, '$1****$3')
                          }
                        </p>
                        <button
                          onClick={() => setShowApiKey(!showApiKey)}
                          className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-white cursor-pointer transition-colors flex-shrink-0"
                          title={showApiKey ? '隐藏' : '显示'}
                        >
                          <i className={`${showApiKey ? 'ri-eye-off-line' : 'ri-eye-line'} text-[#646a73]`}></i>
                        </button>
                      </div>
                      <div className="flex gap-2 mt-3">
                        <button
                          onClick={() => copyToClipboard((apiKeys[0] as any).key || apiKeys[0].key_prefix)}
                          className="flex-1 flex items-center justify-center gap-1.5 py-2 bg-violet-500 text-white text-xs font-medium rounded-lg hover:bg-violet-600 cursor-pointer transition-colors"
                        >
                          {copySuccess ? <><i className="ri-check-line"></i>复制成功</> : <><i className="ri-file-copy-line"></i>复制Key</>}
                        </button>
                        <button
                          onClick={handleCreateApiKey}
                          className="flex-1 flex items-center justify-center gap-1.5 py-2 bg-white border border-violet-300 text-violet-600 text-xs font-medium rounded-lg hover:bg-violet-50 cursor-pointer transition-colors"
                        >
                          <i className="ri-refresh-line"></i>重新生成
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div className="bg-gray-50 rounded-xl p-6 text-center">
                      <i className="ri-key-2-line text-3xl text-gray-300 mb-2"></i>
                      <p className="text-sm text-gray-400 mb-3">暂无API Key</p>
                      <button
                        onClick={handleCreateApiKey}
                        className="px-6 py-2 bg-gradient-to-r from-violet-500 to-purple-600 text-white text-sm font-medium rounded-xl cursor-pointer hover:opacity-90"
                      >
                        <i className="ri-add-line mr-1"></i>生成API Key
                      </button>
                    </div>
                  )}
                </div>

                {/* 退出登录 */}
                <div className="bg-white rounded-2xl p-5">
                  <button
                    onClick={() => setShowLogoutConfirm(true)}
                    className="w-full flex items-center justify-center gap-2 py-3 rounded-xl border-2 border-amber-300 text-amber-600 font-bold text-sm hover:bg-amber-50 active:bg-amber-100 transition-colors cursor-pointer whitespace-nowrap"
                  >
                    <i className="ri-logout-box-r-line text-base"></i>退出当前账号
                  </button>
                </div>
              </div>
            )}

          </div>
        </div>
      </div>

      {/* Change Phone Modal */}
      {showPhoneModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4" onClick={() => setShowPhoneModal(false)}>
          <div className="bg-white rounded-2xl w-full max-w-sm overflow-hidden" onClick={e => e.stopPropagation()}>
            <div className="bg-gradient-to-r from-blue-500 to-blue-700 px-5 py-4 flex items-center justify-between">
              <h3 className="text-white font-bold flex items-center gap-2"><i className="ri-smartphone-fill"></i>更改手机号</h3>
              <button onClick={() => setShowPhoneModal(false)} className="text-white/70 hover:text-white cursor-pointer"><i className="ri-close-line text-xl"></i></button>
            </div>
            <div className="p-5 space-y-3">
              <div>
                <label className="text-xs text-gray-500 mb-1 block">新手机号</label>
                <input value={newPhone} onChange={e => setNewPhone(e.target.value)} placeholder="请输入新手机号" className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:border-blue-400" />
              </div>
              <div>
                <label className="text-xs text-gray-500 mb-1 block">验证码</label>
                <div className="flex gap-2">
                  <input value={verifyCode} onChange={e => setVerifyCode(e.target.value)} placeholder="6位验证码" className="flex-1 border border-gray-200 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:border-blue-400" />
                  <button onClick={startCountdown} disabled={countdown > 0} className={`px-4 py-2.5 rounded-xl text-xs font-medium whitespace-nowrap cursor-pointer ${countdown > 0 ? 'bg-gray-100 text-gray-400 cursor-not-allowed' : 'bg-blue-50 text-blue-500 hover:bg-blue-100'}`}>
                    {countdown > 0 ? `${countdown}s` : '获取验证码'}
                  </button>
                </div>
              </div>
              <button onClick={() => setShowPhoneModal(false)} className="w-full py-2.5 bg-gradient-to-r from-blue-500 to-blue-700 text-white text-sm font-bold rounded-xl cursor-pointer hover:opacity-90 whitespace-nowrap">
                确认更改
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Password Modal */}
      {showPasswordModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4" onClick={() => setShowPasswordModal(false)}>
          <div className="bg-white rounded-2xl w-full max-w-sm overflow-hidden" onClick={e => e.stopPropagation()}>
            <div className="bg-gradient-to-r from-blue-500 to-blue-700 px-5 py-4 flex items-center justify-between">
              <h3 className="text-white font-bold flex items-center gap-2"><i className="ri-lock-password-fill"></i>修改密码</h3>
              <button onClick={() => setShowPasswordModal(false)} className="text-white/70 hover:text-white cursor-pointer"><i className="ri-close-line text-xl"></i></button>
            </div>
            <div className="p-5 space-y-3">
              <div>
                <label className="text-xs text-gray-500 mb-1 block">当前密码</label>
                <input type="password" value={oldPwd} onChange={e => setOldPwd(e.target.value)} placeholder="请输入当前密码" className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:border-blue-400" />
              </div>
              <div>
                <label className="text-xs text-gray-500 mb-1 block">新密码</label>
                <input type="password" value={newPwd} onChange={e => setNewPwd(e.target.value)} placeholder="8位以上，包含字母和数字" className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:border-blue-400" />
              </div>
              <div>
                <label className="text-xs text-gray-500 mb-1 block">确认新密码</label>
                <input type="password" value={confirmPwd} onChange={e => setConfirmPwd(e.target.value)} placeholder="再次输入新密码" className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:border-blue-400" />
              </div>
              <button onClick={handleUpdatePassword} className="w-full py-2.5 bg-gradient-to-r from-blue-500 to-blue-700 text-white text-sm font-bold rounded-xl cursor-pointer hover:opacity-90 whitespace-nowrap">
                确认修改密码
              </button>
            </div>
          </div>
        </div>
      )}

      {/* API Key Modal */}
      {showApiKeyModal && newApiKey && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4" onClick={() => { setShowApiKeyModal(false); setNewApiKey(null); }}>
          <div className="bg-white rounded-2xl w-full max-w-md overflow-hidden" onClick={e => e.stopPropagation()}>
            <div className="bg-gradient-to-r from-violet-500 to-purple-600 px-5 py-4 flex items-center justify-between">
              <h3 className="text-white font-bold flex items-center gap-2"><i className="ri-key-2-fill"></i>API Key已生成</h3>
              <button onClick={() => { setShowApiKeyModal(false); setNewApiKey(null); }} className="text-white/70 hover:text-white cursor-pointer"><i className="ri-close-line text-xl"></i></button>
            </div>
            <div className="p-5">
              <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 mb-4">
                <div className="flex items-start gap-2">
                  <i className="ri-alert-fill text-amber-500 text-lg mt-0.5"></i>
                  <div>
                    <p className="text-sm font-semibold text-amber-800">请立即保存您的API Key</p>
                    <p className="text-xs text-amber-600 mt-1">此Key只会显示一次，关闭后将无法再次查看完整Key。</p>
                  </div>
                </div>
              </div>

              <div className="bg-gray-50 rounded-xl p-4 mb-4">
                <label className="text-xs text-gray-500 mb-2 block">您的API Key</label>
                <div className="flex items-center gap-2">
                  <code className="flex-1 text-sm font-mono bg-white px-3 py-2 rounded-lg border border-gray-200 break-all">
                    {newApiKey}
                  </code>
                  <button
                    onClick={() => copyToClipboard(newApiKey)}
                    className="px-3 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 cursor-pointer whitespace-nowrap flex items-center gap-1.5"
                  >
                    {copySuccess ? <><i className="ri-check-line"></i>已复制</> : <><i className="ri-file-copy-line"></i>复制</>}
                  </button>
                </div>
              </div>

              <div className="text-xs text-gray-400 space-y-1.5">
                <p className="flex items-center gap-1.5"><i className="ri-information-line text-blue-500"></i>使用方法：</p>
                <ol className="list-decimal list-inside space-y-1 ml-4">
                  <li>复制上面的API Key</li>
                  <li>在Claude Desktop配置文件中添加此Key</li>
                  <li>重启Claude Desktop即可使用</li>
                </ol>
              </div>

              <button
                onClick={() => { setShowApiKeyModal(false); setNewApiKey(null); }}
                className="w-full mt-4 py-2.5 bg-gradient-to-r from-violet-500 to-purple-600 text-white text-sm font-bold rounded-xl cursor-pointer hover:opacity-90 whitespace-nowrap"
              >
                我已保存，关闭
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Logout Confirm Modal */}
      {showLogoutConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4" onClick={() => setShowLogoutConfirm(false)}>
          <div className="bg-white rounded-2xl p-6 w-full max-w-[360px] text-center" onClick={e => e.stopPropagation()}>
            <div className="w-16 h-16 flex items-center justify-center rounded-full bg-amber-50 mx-auto mb-4">
              <i className="ri-logout-box-r-fill text-amber-500 text-2xl"></i>
            </div>
            <h3 className="font-bold text-blue-900 text-lg mb-2">确认退出登录？</h3>
            <p className="text-gray-400 text-sm mb-5">退出后需要重新登录才能使用习惯星球的所有功能</p>
            <div className="flex gap-3">
              <button
                onClick={() => setShowLogoutConfirm(false)}
                className="flex-1 py-2.5 rounded-xl border-2 border-gray-200 text-gray-600 text-sm font-medium hover:bg-gray-50 cursor-pointer whitespace-nowrap"
              >
                取消
              </button>
              <button
                onClick={() => { logout(); navigate('/'); }}
                className="flex-1 py-2.5 rounded-xl bg-gradient-to-r from-amber-400 to-orange-500 text-white text-sm font-bold hover:opacity-90 cursor-pointer whitespace-nowrap"
              >
                确认退出
              </button>
            </div>
          </div>
        </div>
      )}
    </DashboardLayout>
  );
}
